import React, { useEffect, useMemo, useReducer, useRef, useState } from "react";
import ReactDOM from "react-dom";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  closestCenter,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  horizontalListSortingStrategy,
  rectSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { motion, AnimatePresence } from "framer-motion";
import { Toaster, toast } from "sonner";
import {
  Building2, Plus, Lock, Unlock, Save, GripVertical, Settings2, Zap,
  Edit3, Trash2, Sparkles, Copy, Pencil, X, Bed as BedIcon,
} from "lucide-react";

/* ============================================================
   TYPES / CONSTANTS
   ============================================================ */

export const ROOM_TYPES = [
  { key: "Single", label: "Single", beds: 1, color: "#10B981", bg: "#D1FAE5" },
  { key: "Double", label: "Double", beds: 2, color: "#3B82F6", bg: "#DBEAFE" },
  { key: "Triple", label: "Triple", beds: 3, color: "#8B5CF6", bg: "#EDE9FE" },
  { key: "Quad",   label: "Quad",   beds: 4, color: "#F59E0B", bg: "#FEF3C7" },
  { key: "Dorm",   label: "Dorm",   beds: 6, color: "#EF4444", bg: "#FEE2E2" },
];

const WASHROOM_OPTIONS  = ["Attached", "Common", "No"];
const FURNITURE_OPTIONS = ["Yes", "No", "Provided"];
const COOLING_OPTIONS   = ["AC", "Cooler", "Fan", "None"];
const GEYSER_OPTIONS    = ["Yes", "No"];
const WASHING_OPTIONS   = ["Yes", "No", "Common"];
const INDUCTION_OPTIONS = ["Yes", "No"];
const WINDOW_OPTIONS    = ["Yes", "No", "Corner"];
const BALCONY_OPTIONS   = ["Yes", "No"];
const BED_STATUS_OPTIONS = ["AVAILABLE", "BOOKED", "OCCUPIED", "MAINTENANCE"];

const DEFAULT_AMENITIES = {
  washroom: "Attached", furniture: "Yes", cooling: "Fan", geyser: "Yes",
  washing: "Common",    induction: "No",  window:  "Yes", balcony: "No",
};

const DEFAULT_ELECTRICITY = {
  meter: "", previousReading: 0, currentReading: 0,
  month: new Date().getMonth() + 1, year: new Date().getFullYear(), unitCost: 10,
};

const DEFAULT_AMENITY_PRESETS = {
  Single: { ...DEFAULT_AMENITIES, washroom: "Attached", cooling: "AC" },
  Double: { ...DEFAULT_AMENITIES },
  Triple: { ...DEFAULT_AMENITIES, washroom: "Common" },
  Quad:   { ...DEFAULT_AMENITIES, washroom: "Common", cooling: "Cooler" },
  Dorm:   { ...DEFAULT_AMENITIES, washroom: "Common", cooling: "Fan" },
};

const ORDINALS = ["Ground", "1st", "2nd", "3rd", "4th", "5th", "6th", "7th", "8th", "9th"];
const FLOOR_PRESET = (index) => ({
  id: `floor-${index}-${Math.random().toString(36).slice(2, 7)}`,
  label: index === 0 ? "Ground Floor" : `${ORDINALS[index] ?? `${index}th`} Floor`,
  prefix: String(index + 1),
});

const genBedLabel = (i) => `B-${String(i + 1).padStart(2, "0")}`;
const genRoomNumber = (prefix, idx) => `${prefix}${String(idx + 1).padStart(2, "0")}`;

const makeBeds = (count) =>
  Array.from({ length: count }, (_, i) => ({
    id: `bed-${Math.random().toString(36).slice(2, 9)}`,
    label: genBedLabel(i),
    status: "AVAILABLE",
    price: 8000,
  }));

const findRoomType = (key) => ROOM_TYPES.find((r) => r.key === key);

/**
 * Sequential auto-numbering: rewrites room.number for one floor based on
 * the room's position in the rooms array. Called after every structural
 * change so numbers always match the visual order on the blueprint.
 */
function renumberFloor(rooms, floors, floorId, pad) {
  const floor = floors.find((f) => f.id === floorId);
  if (!floor) return rooms;
  let seq = 0;
  return rooms.map((r) => {
    if (r.floorId !== floorId) return r;
    seq += 1;
    const next = `${floor.prefix}${String(seq).padStart(pad, "0")}`;
    return r.number === next ? r : { ...r, number: next, dirty: true };
  });
}


/* ============================================================
   NUMBERING HELPERS
   ============================================================ */

function parseForFloor(num, floor) {
  if (!num || !num.startsWith(floor.prefix)) return null;
  const tail = num.slice(floor.prefix.length);
  if (!/^\d+$/.test(tail)) return null;
  return { n: parseInt(tail, 10), pad: tail.length };
}

function nextNumberForFloor(rooms, floor, pad) {
  const fr = rooms.filter((r) => r.floorId === floor.id && r.number);
  let max = 0;
  for (const r of fr) {
    const t = parseForFloor(r.number, floor);
    if (t) max = Math.max(max, t.n);
  }
  return `${floor.prefix}${String((max || 0) + 1).padStart(pad, "0")}`;
}

function makeNumberSequence(startNumber, count, floor) {
  const parsed = parseForFloor(String(startNumber).trim(), floor);
  if (!parsed) return null;
  return Array.from({ length: count }, (_, i) =>
    `${floor.prefix}${String(parsed.n + i).padStart(parsed.pad, "0")}`,
  );
}

function validateNumber(candidate, floor, scheme, rooms, excludeRoomId) {
  const trimmed = String(candidate).trim();
  if (!trimmed) return { ok: false, reason: "empty", message: "Room number can't be blank." };
  const t = parseForFloor(trimmed, floor);
  if (!t)
    return {
      ok: false, reason: "empty",
      message: `Use the ${floor.label} pattern, e.g. ${floor.prefix}${"1".padStart(scheme.pad, "0")}.`,
    };
  const dup = rooms.find((r) => r.id !== excludeRoomId && r.number === trimmed);
  if (dup) return { ok: false, reason: "duplicate", message: `Number "${trimmed}" is already used.` };
  if (t.pad !== scheme.pad)
    return {
      ok: false, reason: "pad-mismatch",
      message: `PG uses ${scheme.pad}-digit numbering. Apply ${t.pad}-digit across the PG?`,
      newPad: t.pad,
    };
  return { ok: true };
}

/* ============================================================
   STATE (useReducer + localStorage)
   ============================================================ */

const STORAGE_KEY = "aapkapg-planner-v1";

const initialFloors = [FLOOR_PRESET(0)];
const initialState = {
  pgName: "Guruji PG",
  floors: initialFloors,
  rooms: [],
  amenityPresets: DEFAULT_AMENITY_PRESETS,
  electricityPreset: { ...DEFAULT_ELECTRICITY, unitCost: 10 },
  locked: false,
  activeFloorId: initialFloors[0].id,
  numberingPad: 2,
};

function loadState() {
  if (typeof window === "undefined") return initialState;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return initialState;
    const saved = JSON.parse(raw);
    return { ...initialState, ...saved, locked: false };
  } catch {
    return initialState;
  }
}

function persist(state) {
  if (typeof window === "undefined") return;
  const { locked, ...rest } = state;
  try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(rest)); } catch {}
}

function reducer(state, action) {
  switch (action.type) {
    case "SET_ACTIVE_FLOOR":
      return { ...state, activeFloorId: action.id };

    case "TOGGLE_LOCK":
      return { ...state, locked: !state.locked };

    case "SET_NUMBERING_PAD": {
      const p = Math.max(1, Math.min(6, Math.round(action.pad)));
      return {
        ...state,
        numberingPad: p,
        rooms: state.rooms.map((r) => {
          const m = r.number?.match(/^(.*?)(\d+)$/);
          if (!m) return r;
          const newNum = `${m[1]}${String(parseInt(m[2], 10)).padStart(p, "0")}`;
          return newNum === r.number ? r : { ...r, number: newNum, dirty: true };
        }),
      };
    }

    case "ADD_FLOOR": {
      const next = FLOOR_PRESET(state.floors.length);
      return { ...state, floors: [...state.floors, next], activeFloorId: next.id };
    }

    case "REMOVE_FLOOR": {
      // Remove all rooms belonging to this floor
      const remainingRooms = state.rooms.filter((r) => r.floorId !== action.id);
      const floors = state.floors.filter((f) => f.id !== action.id);
      return {
        ...state,
        floors,
        rooms: remainingRooms,
        activeFloorId: state.activeFloorId === action.id ? (floors[0]?.id ?? "") : state.activeFloorId,
      };
    }

    case "ADD_ROOMS_BULK": {
      const { floorId, roomType, count, opts } = action;
      if (count <= 0) return state;
      const meta = findRoomType(roomType);

      const newRooms = Array.from({ length: count }, () => ({
        id: `r-${Math.random().toString(36).slice(2, 9)}`,
        floorId,
        type: roomType,
        beds: makeBeds(meta.beds),
        amenities: { ...state.amenityPresets[roomType] },
        amenitiesOverridden: false,
        electricity: { ...state.electricityPreset },
        electricityOverridden: false,
        isNew: true, dirty: true,
      }));

      const floorRooms = state.rooms.filter((r) => r.floorId === floorId);
      const at = opts?.atIndex;
      const insertAt = at !== undefined && at >= 0 && at <= floorRooms.length ? at : floorRooms.length;
      const before = floorRooms.slice(0, insertAt);
      const after = floorRooms.slice(insertAt);
      const others = state.rooms.filter((r) => r.floorId !== floorId);
      const merged = [...others, ...before, ...newRooms, ...after];
      return { ...state, rooms: renumberFloor(merged, state.floors, floorId, state.numberingPad) };
    }

    case "DUPLICATE_FLOOR": {
      const src = state.floors.find((f) => f.id === action.floorId);
      if (!src) return state;
      const newFloor = FLOOR_PRESET(state.floors.length);
      const srcRooms = state.rooms.filter((r) => r.floorId === action.floorId);
      const cloned = srcRooms.map((r) => ({
        ...r,
        id: `r-${Math.random().toString(36).slice(2, 9)}`,
        floorId: newFloor.id,
        number: undefined,
        beds: r.beds.map((b, i) => ({
          ...b,
          id: `bed-${Math.random().toString(36).slice(2, 9)}`,
          label: genBedLabel(i),
          status: "AVAILABLE",
        })),
        isNew: true, dirty: true,
      }));
      const floors = [...state.floors, newFloor];
      const rooms = renumberFloor([...state.rooms, ...cloned], floors, newFloor.id, state.numberingPad);
      return { ...state, floors, rooms, activeFloorId: newFloor.id };
    }

    case "DELETE_ROOM": {
      const target = state.rooms.find((r) => r.id === action.roomId);
      const next = state.rooms.filter((r) => r.id !== action.roomId);
      if (!target) return { ...state, rooms: next };
      return { ...state, rooms: renumberFloor(next, state.floors, target.floorId, state.numberingPad) };
    }

    case "SET_ROOM_NUMBER":
      return {
        ...state,
        rooms: state.rooms.map((r) =>
          r.id === action.roomId
            ? { ...r, number: action.number.trim() || undefined, dirty: true }
            : r,
        ),
      };

    case "REORDER_ROOM": {
      if (state.locked) return state;
      const { floorId, fromIdx, toIdx } = action;
      const floorRooms = state.rooms.filter((r) => r.floorId === floorId);
      if (fromIdx === toIdx || fromIdx < 0 || toIdx < 0) return state;
      const reordered = [...floorRooms];
      const [moved] = reordered.splice(fromIdx, 1);
      reordered.splice(toIdx, 0, moved);
      const others = state.rooms.filter((r) => r.floorId !== floorId);
      const merged = [...others, ...reordered];
      return { ...state, rooms: renumberFloor(merged, state.floors, floorId, state.numberingPad) };
    }

    case "MOVE_ROOM_TO_FLOOR": {
      if (state.locked) return state;
      const { roomId, toFloorId, atIndex } = action;
      const room = state.rooms.find((r) => r.id === roomId);
      if (!room || room.floorId === toFloorId) return state;
      const fromFloorId = room.floorId;
      const without = state.rooms.filter((r) => r.id !== roomId);
      const target = without.filter((r) => r.floorId === toFloorId);
      const insertAt = atIndex !== undefined && atIndex >= 0 && atIndex <= target.length ? atIndex : target.length;
      const before = target.slice(0, insertAt);
      const after = target.slice(insertAt);
      const others = without.filter((r) => r.floorId !== toFloorId);
      const merged = [...others, ...before, { ...room, floorId: toFloorId, dirty: true }, ...after];
      const step1 = renumberFloor(merged, state.floors, toFloorId, state.numberingPad);
      const step2 = renumberFloor(step1, state.floors, fromFloorId, state.numberingPad);
      return { ...state, rooms: step2 };
    }


    case "UPDATE_ROOM_AMENITIES":
      return {
        ...state,
        rooms: state.rooms.map((r) =>
          r.id === action.roomId
            ? { ...r, amenities: action.amenities, amenitiesOverridden: true, dirty: true }
            : r,
        ),
      };

    case "UPDATE_ROOM_ELECTRICITY":
      return {
        ...state,
        rooms: state.rooms.map((r) =>
          r.id === action.roomId
            ? { ...r, electricity: action.electricity, electricityOverridden: true, dirty: true }
            : r,
        ),
      };

    case "UPDATE_BED":
      return {
        ...state,
        rooms: state.rooms.map((r) =>
          r.id === action.roomId
            ? {
                ...r,
                beds: r.beds.map((b) => (b.id === action.bedId ? { ...b, ...action.patch } : b)),
                dirty: true,
              }
            : r,
        ),
      };

    case "SET_AMENITY_PRESET":
      return {
        ...state,
        amenityPresets: { ...state.amenityPresets, [action.roomType]: action.amenities },
      };

    case "SET_ELECTRICITY_PRESET":
      return { ...state, electricityPreset: action.electricity };

    case "SAVE_ALL":
      return {
        ...state,
        rooms: state.rooms.map((r) => ({ ...r, isNew: false, dirty: false })),
      };

    default:
      return state;
  }
}

/* ============================================================
   MODAL PRIMITIVE
   ============================================================ */

function Modal({ open, onClose, title, subtitle, children, footer, size = "md" }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open || typeof document === "undefined") return null;

  return ReactDOM.createPortal(
    <div className="owner-planning__modal-overlay" onMouseDown={onClose}>
      <div
        className={`owner-planning__modal owner-planning__modal--${size}`}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="owner-planning__modal-header">
          <div>
            {title && <h3 className="owner-planning__modal-title">{title}</h3>}
            {subtitle && <p className="owner-planning__modal-subtitle">{subtitle}</p>}
          </div>
          <button className="owner-planning__icon-btn" onClick={onClose} aria-label="Close">
            <X size={16} />
          </button>
        </div>
        <div className="owner-planning__modal-body">{children}</div>
        {footer && <div className="owner-planning__modal-footer">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}

/* ============================================================
   DELETE FLOOR CONFIRMATION DIALOG
   ============================================================ */

function DeleteFloorConfirmDialog({ floor, rooms, onConfirm, onClose }) {
  const roomCount = rooms?.length || 0;
  
  if (!floor) return null;
  
  return (
    <Modal
      open={!!floor}
      onClose={onClose}
      title="Delete Entire Floor?"
      size="sm"
      footer={
        <>
          <button className="owner-planning__btn owner-planning__btn--outline" onClick={onClose}>
            Cancel
          </button>
          <button 
            className="owner-planning__btn owner-planning__btn--danger" 
            onClick={() => onConfirm(floor.id)}
          >
            Delete Floor & All Rooms
          </button>
        </>
      }
    >
      <div className="owner-planning__delete-warning">
        <p className="owner-planning__modal-text" style={{ marginBottom: '12px' }}>
          You are about to delete <strong>{floor.label}</strong> which contains:
        </p>
        <ul style={{ margin: '8px 0 0 20px', color: '#64748B' }}>
          <li><strong>{roomCount}</strong> room{roomCount !== 1 ? 's' : ''}</li>
          <li><strong>{rooms?.reduce((sum, r) => sum + r.beds.length, 0) || 0}</strong> bed{rooms?.reduce((sum, r) => sum + r.beds.length, 0) !== 1 ? 's' : ''}</li>
        </ul>
        <p className="owner-planning__modal-text" style={{ marginTop: '16px', color: '#EF4444', fontWeight: 500 }}>
          ⚠️ This action cannot be undone. All rooms and beds on this floor will be permanently deleted.
        </p>
      </div>
    </Modal>
  );
}

/* ============================================================
   MAIN COMPONENT
   ============================================================ */

export default function OwnerPlanning() {
  const [state, dispatch] = useReducer(reducer, undefined, loadState);
  useEffect(() => { persist(state); }, [state]);

  const [editingRoom, setEditingRoom] = useState(null);
  const [bulkRequest, setBulkRequest] = useState(null);
  const [presetsOpen, setPresetsOpen] = useState(null);
  const [activeDrag, setActiveDrag] = useState(null);
  const [floorToDelete, setFloorToDelete] = useState(null);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const stats = useMemo(() => ({
    rooms: state.rooms.length,
    beds: state.rooms.reduce((s, r) => s + r.beds.length, 0),
    unsaved: state.rooms.filter((r) => r.dirty || r.isNew).length,
  }), [state.rooms]);

  const floorRoomsByFloor = useMemo(() => {
    const m = {};
    for (const f of state.floors) m[f.id] = state.rooms.filter((r) => r.floorId === f.id);
    return m;
  }, [state.floors, state.rooms]);

  const onDragStart = (e) => {
    const d = e.active.data.current;
    if (!d) return;
    if (d.source === "palette") setActiveDrag({ kind: "palette", type: d.type });
    else setActiveDrag({ kind: "room", roomId: d.roomId });
  };

  const onDragEnd = (e) => {
    setActiveDrag(null);
    const { active, over } = e;
    if (!over) return;
    const aData = active.data.current;
    if (!aData) return;

    const overId = String(over.id);
    const overData = over.data.current;

    let targetFloorId = null;
    let targetIndex;

    if (overId.startsWith("floor-")) {
      targetFloorId = overData?.floorId ?? overId.replace(/^floor-/, "");
    } else {
      const targetRoom = state.rooms.find((r) => r.id === overId);
      if (targetRoom) {
        targetFloorId = targetRoom.floorId;
        const fr = state.rooms.filter((r) => r.floorId === targetFloorId);
        targetIndex = fr.findIndex((r) => r.id === overId);
      }
    }
    if (!targetFloorId) return;

    if (aData.source === "palette") {
      setBulkRequest({ floorId: targetFloorId, type: aData.type, atIndex: targetIndex });
      return;
    }
    if (aData.source === "room") {
      if (aData.floorId === targetFloorId) {
        const fr = state.rooms.filter((r) => r.floorId === targetFloorId);
        const fromIdx = fr.findIndex((r) => r.id === aData.roomId);
        const toIdx = targetIndex ?? fr.length - 1;
        if (fromIdx !== -1 && toIdx !== -1) {
          dispatch({ type: "REORDER_ROOM", floorId: targetFloorId, fromIdx, toIdx });
        }
      } else {
        dispatch({ type: "MOVE_ROOM_TO_FLOOR", roomId: aData.roomId, toFloorId: targetFloorId, atIndex: targetIndex });
      }
    }
  };

  const handleSave = () => {
    dispatch({ type: "SAVE_ALL" });
    const payload = {
      pgName: state.pgName,
      numberingPad: state.numberingPad,
      amenityPresets: state.amenityPresets,
      electricityPreset: state.electricityPreset,
      floors: state.floors.map((f) => ({
        ...f,
        rooms: state.rooms
          .filter((r) => r.floorId === f.id)
          .map((r, i) => ({
            id: r.id,
            number: r.number || genRoomNumber(f.prefix, i),
            type: r.type,
            beds: r.beds,
            amenities: r.amenities,
            amenitiesOverridden: r.amenitiesOverridden,
            electricity: r.electricity,
            electricityOverridden: r.electricityOverridden,
          })),
      })),
    };
    // eslint-disable-next-line no-console
    console.log("[OwnerPlanning] Save payload →", payload);
    toast.success("Layout saved", {
      description: `${stats.rooms} rooms · ${stats.beds} beds committed.`,
    });
  };

  const sortedFloorsForStack = useMemo(
    () => [...state.floors].sort((a, b) => Number(b.prefix) - Number(a.prefix)),
    [state.floors],
  );

  return (
    <div className="owner-planning">
      <PlannerHeader stats={stats} locked={state.locked} dispatch={dispatch} onSave={handleSave} />

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        onDragCancel={() => setActiveDrag(null)}
      >
        <div className="owner-planning__layout">
          <RoomTypePalette onConfigure={setPresetsOpen} />

          <section className="owner-planning__board">
            <div className="owner-planning__floor-tabs">
              {state.floors.map((f) => (
                <FloorTab
                  key={f.id}
                  label={f.label}
                  count={floorRoomsByFloor[f.id]?.length ?? 0}
                  active={f.id === state.activeFloorId}
                  onSelect={() => dispatch({ type: "SET_ACTIVE_FLOOR", id: f.id })}
                />
              ))}
              <button
                className="owner-planning__add-floor"
                onClick={() => dispatch({ type: "ADD_FLOOR" })}
              >
                <Plus size={14} /> Add Floor
              </button>
            </div>

            <div className="owner-planning__floor-stack">
              {sortedFloorsForStack.map((f) => (
                <FloorStrip
                  key={f.id}
                  floor={f}
                  rooms={floorRoomsByFloor[f.id] ?? []}
                  locked={state.locked}
                  allRooms={state.rooms}
                  numberingPad={state.numberingPad}
                  dispatch={dispatch}
                  onEditRoom={setEditingRoom}
                  onDeleteFloor={(floorId) => {
                    const floor = state.floors.find(f => f.id === floorId);
                    const floorRooms = state.rooms.filter(r => r.floorId === floorId);
                    setFloorToDelete({ floor, rooms: floorRooms });
                  }}
                />
              ))}
            </div>
          </section>
        </div>

        <DragOverlay dropAnimation={null}>
          {activeDrag ? <DragPreview drag={activeDrag} rooms={state.rooms} /> : null}
        </DragOverlay>
      </DndContext>

      <RoomEditDialog
        room={editingRoom ? state.rooms.find((r) => r.id === editingRoom) : null}
        floors={state.floors}
        rooms={state.rooms}
        amenityPresets={state.amenityPresets}
        electricityPreset={state.electricityPreset}
        dispatch={dispatch}
        onClose={() => setEditingRoom(null)}
      />

      <BulkAddDialog
        request={bulkRequest}
        floors={state.floors}
        rooms={state.rooms}
        numberingPad={state.numberingPad}
        dispatch={dispatch}
        onClose={() => setBulkRequest(null)}
      />

      <PresetsDialog
        target={presetsOpen}
        amenityPresets={state.amenityPresets}
        electricityPreset={state.electricityPreset}
        dispatch={dispatch}
        onClose={() => setPresetsOpen(null)}
      />

      <DeleteFloorConfirmDialog
        floor={floorToDelete?.floor}
        rooms={floorToDelete?.rooms}
        onConfirm={(floorId) => {
          dispatch({ type: "REMOVE_FLOOR", id: floorId });
          toast.success(`${floorToDelete?.floor?.label} deleted with all its rooms`);
          setFloorToDelete(null);
        }}
        onClose={() => setFloorToDelete(null)}
      />

      <Toaster position="bottom-right" />
    </div>
  );
}

/* ============================================================
   HEADER
   ============================================================ */

const BED_LEGEND = [
  ["Available", "available"],
  ["Booked", "booked"],
  ["Occupied", "occupied"],
  ["Maintenance", "maintenance"],
];

function PlannerHeader({ stats, locked, dispatch, onSave }) {
  return (
    <div className="owner-planning__header">
      <div className="owner-planning__header-row">
        <div>
          <h1 className="owner-planning__title">PG Layout Planner</h1>
          <p className="owner-planning__subtitle">
            Drag room types onto a floor to start planning
          </p>
        </div>
        <div className="owner-planning__header-actions">
          <button
            onClick={() => dispatch({ type: "TOGGLE_LOCK" })}
            title={locked ? "Locked — click to unlock" : "Unlocked — click to lock"}
            className={`owner-planning__lock owner-planning__lock--${locked ? "locked" : "unlocked"}`}
          >
            {locked ? <Lock size={14} /> : <Unlock size={14} />}
            {locked ? "Locked" : "Unlocked"}
          </button>
          <motion.button
            whileTap={{ scale: 0.97 }}
            className="owner-planning__save"
            onClick={onSave}
          >
            <Save size={16} />
            Save ({stats.unsaved})
          </motion.button>
        </div>
      </div>

      <div className="owner-planning__legend-row">
        <ul className="owner-planning__legend">
          {BED_LEGEND.map(([label, key]) => (
            <li key={label} className="owner-planning__legend-item">
              <span className={`owner-planning__dot owner-planning__dot--${key}`} />
              {label}
            </li>
          ))}
        </ul>
        <div className="owner-planning__stats">
          <span>Rooms: <b>{stats.rooms}</b></span>
          <span>Beds: <b>{stats.beds}</b></span>
          <span>Unsaved: <b className={stats.unsaved > 0 ? "owner-planning__accent" : ""}>{stats.unsaved}</b></span>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   PALETTE
   ============================================================ */

function RoomTypePalette({ onConfigure }) {
  return (
    <aside className="owner-planning__palette">
      <div className="owner-planning__palette-card">
        <div className="owner-planning__palette-header">
          <div>
            <h3 className="owner-planning__palette-title">Room Types</h3>
            <p className="owner-planning__palette-hint">Drag onto a floor · tap ⚙ to set defaults</p>
          </div>
        </div>

        <div className="owner-planning__palette-list">
          {ROOM_TYPES.map((t) => (
            <DraggableType
              key={t.key}
              type={t}
              onConfigure={() => onConfigure({ kind: "amenities", type: t.key })}
            />
          ))}
        </div>

        <button
          className="owner-planning__electricity-btn"
          onClick={() => onConfigure({ kind: "electricity" })}
        >
          <span><Zap size={14} /> Electricity defaults</span>
          <Settings2 size={14} />
        </button>
      </div>
    </aside>
  );
}

function DraggableType({ type, onConfigure }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `palette-${type.key}`,
    data: { source: "palette", type: type.key },
  });
  return (
    <motion.div
      animate={{ opacity: isDragging ? 0.4 : 1 }}
      className="owner-planning__palette-item"
      style={{ background: type.bg, borderColor: `${type.color}33` }}
    >
      <div
        ref={setNodeRef}
        {...listeners}
        {...attributes}
        className="owner-planning__palette-grab"
      >
        <GripVertical size={14} className="owner-planning__palette-grip" />
        <div className="owner-planning__palette-beds">
          {Array.from({ length: Math.min(type.beds, 4) }).map((_, i) => (
            <span key={i} style={{ background: type.color }} />
          ))}
          {type.beds > 4 && <span className="owner-planning__palette-bedcount" style={{ color: type.color }}>×{type.beds}</span>}
        </div>
        <div>
          <div className="owner-planning__palette-label" style={{ color: type.color }}>{type.label}</div>
          <div className="owner-planning__palette-sublabel">{type.beds} bed{type.beds > 1 ? "s" : ""}</div>
        </div>
      </div>
      <button
        onClick={onConfigure}
        className="owner-planning__icon-btn"
        title={`${type.label} defaults`}
      >
        <Settings2 size={14} />
      </button>
    </motion.div>
  );
}

/* ============================================================
   FLOOR TAB
   ============================================================ */

function FloorTab({ label, count, active, onSelect }) {
  return (
    <button
      onClick={onSelect}
      className={`owner-planning__floor-tab ${active ? "owner-planning__floor-tab--active" : ""}`}
    >
      {label}
      <span className={`owner-planning__floor-tab-count ${active ? "owner-planning__floor-tab-count--active" : ""}`}>
        {count}
      </span>
    </button>
  );
}

/* ============================================================
   FLOOR STRIP
   ============================================================ */

function FloorStrip({ floor, rooms, locked, allRooms, numberingPad, dispatch, onEditRoom, onDeleteFloor }) {
  const { setNodeRef, isOver } = useDroppable({
    id: `floor-${floor.id}`,
    data: { floorId: floor.id },
  });
  const totalBeds = rooms.reduce((s, r) => s + r.beds.length, 0);

  return (
    <div className="owner-planning__floor">
      <div className="owner-planning__floor-head">
        <div className="owner-planning__floor-head-left">
          <div className="owner-planning__floor-badge">{floor.prefix}</div>
          <div>
            <h3 className="owner-planning__floor-title">{floor.label}</h3>
            <p className="owner-planning__floor-meta">{rooms.length} rooms · {totalBeds} beds</p>
          </div>
        </div>
        <div className="owner-planning__floor-head-right">
          <button
            disabled={rooms.length === 0}
            title={rooms.length === 0 ? "Add rooms before duplicating" : "Duplicate this floor"}
            className="owner-planning__floor-action"
            onClick={() => dispatch({ type: "DUPLICATE_FLOOR", floorId: floor.id })}
          >
            <Copy size={14} /> Duplicate
          </button>
          <button
            title="Delete this floor"
            className="owner-planning__icon-btn owner-planning__icon-btn--danger"
            onClick={() => onDeleteFloor(floor.id)}
          >
            <Trash2 size={14} />
          </button>
        </div>
      </div>

      <div
        ref={setNodeRef}
        className={`owner-planning__blueprint ${isOver ? "owner-planning__blueprint--over" : ""}`}
      >
        <div className="owner-planning__corridor" />
        <SortableContext
          items={rooms.map((r) => r.id)}
          strategy={rooms.length > 8 ? rectSortingStrategy : horizontalListSortingStrategy}
          disabled={locked}
        >
          <div className="owner-planning__rooms">
            <AnimatePresence mode="popLayout">
              {rooms.map((r, i) => (
                <SortableRoom
                  key={r.id}
                  room={r}
                  index={i}
                  floor={floor}
                  allRooms={allRooms}
                  numberingPad={numberingPad}
                  dispatch={dispatch}
                  onEdit={() => onEditRoom(r.id)}
                />
              ))}
            </AnimatePresence>
            {(rooms.length === 0 || isOver) && (
              <div className="owner-planning__drop-hint">Drop a room type here</div>
            )}
          </div>
        </SortableContext>
      </div>
    </div>
  );
}

/* ============================================================
   SORTABLE ROOM
   ============================================================ */

function SortableRoom({ room, index, floor, allRooms, numberingPad, dispatch, onEdit }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: room.id,
    data: { source: "room", roomId: room.id, floorId: room.floorId },
  });
  const meta = findRoomType(room.type);
  const autoNumber = genRoomNumber(floor.prefix, index);
  const roomNumber = room.number || autoNumber;

  const [editingNum, setEditingNum] = useState(false);
  const [draftNum, setDraftNum] = useState(roomNumber);
  const [padPrompt, setPadPrompt] = useState(null);

  const commitRename = () => {
    const trimmed = draftNum.trim();
    if (trimmed === roomNumber) { setEditingNum(false); return; }
    const res = validateNumber(trimmed, floor, { pad: numberingPad }, allRooms, room.id);
    if (res.ok) {
      dispatch({ type: "SET_ROOM_NUMBER", roomId: room.id, number: trimmed });
      setEditingNum(false);
      return;
    }
    if (res.reason === "pad-mismatch") {
      setPadPrompt({ value: trimmed, newPad: res.newPad });
      return;
    }
    toast.error(res.message);
    setDraftNum(roomNumber);
    setEditingNum(false);
  };

  const widthUnit = Math.min(meta.beds, 4);
  const width = 132 + (widthUnit - 1) * 56;

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    width,
  };

  return (
    <>
      <motion.div
        layout
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: isDragging ? 0.4 : 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.85 }}
        ref={setNodeRef}
        style={style}
        {...attributes}
        {...listeners}
        className="owner-planning__room"
      >
        <div className="owner-planning__room-stripe" style={{ background: meta.color }} />
        <div className="owner-planning__room-top">
          <div
            className="owner-planning__room-id"
            onPointerDown={(e) => editingNum && e.stopPropagation()}
          >
            {editingNum ? (
              <input
                autoFocus
                value={draftNum}
                onChange={(e) => setDraftNum(e.target.value)}
                onBlur={commitRename}
                onKeyDown={(e) => {
                  if (e.key === "Enter") commitRename();
                  if (e.key === "Escape") { setDraftNum(roomNumber); setEditingNum(false); }
                }}
                className="owner-planning__room-number-input"
              />
            ) : (
              <button
                type="button"
                title="Click to rename"
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  setDraftNum(roomNumber);
                  setEditingNum(true);
                }}
                className="owner-planning__room-number"
              >
                {roomNumber}
                <Pencil size={10} />
              </button>
            )}
            <span
              className="owner-planning__room-typechip"
              style={{ background: meta.bg, color: meta.color }}
            >
              {meta.label}
            </span>
          </div>
          {room.isNew && <span className="owner-planning__new-badge">New</span>}
        </div>

        {!room.amenitiesOverridden && (
          <div className="owner-planning__preset-hint">
            <Sparkles size={10} /> using preset
          </div>
        )}

        <div className="owner-planning__bedchips">
          {room.beds.map((b) => <BedChip key={b.id} bed={b} />)}
        </div>

        <div className="owner-planning__room-bottom">
          <span className="owner-planning__room-free">
            {room.beds.filter((b) => b.status === "AVAILABLE").length} free
          </span>
          <div
            className="owner-planning__room-actions"
            onPointerDown={(e) => e.stopPropagation()}
          >
            <button onClick={onEdit} className="owner-planning__icon-btn"><Edit3 size={12} /></button>
            <button
              onClick={() => dispatch({ type: "DELETE_ROOM", roomId: room.id })}
              className="owner-planning__icon-btn owner-planning__icon-btn--danger"
            >
              <Trash2 size={12} />
            </button>
          </div>
        </div>
      </motion.div>

      <Modal
        open={!!padPrompt}
        onClose={() => { setPadPrompt(null); setDraftNum(roomNumber); setEditingNum(false); }}
        title="Update PG numbering pattern?"
        size="sm"
        footer={
          <>
            <button
              className="owner-planning__btn owner-planning__btn--outline"
              onClick={() => { setPadPrompt(null); setDraftNum(roomNumber); setEditingNum(false); }}
            >
              Cancel
            </button>
            <button
              className="owner-planning__btn owner-planning__btn--accent"
              onClick={() => {
                if (!padPrompt) return;
                dispatch({ type: "SET_NUMBERING_PAD", pad: padPrompt.newPad });
                dispatch({ type: "SET_ROOM_NUMBER", roomId: room.id, number: padPrompt.value });
                toast.success(`Switched to ${padPrompt.newPad}-digit numbering across the PG`);
                setPadPrompt(null);
                setEditingNum(false);
              }}
            >
              Apply across PG
            </button>
          </>
        }
      >
        <p className="owner-planning__modal-text">
          Your PG currently uses <b>{numberingPad}-digit</b> room numbers. You entered{" "}
          <b>"{padPrompt?.value}"</b> which is <b>{padPrompt?.newPad}-digit</b>. Room numbers should
          stay consistent across the PG.
        </p>
      </Modal>
    </>
  );
}

function BedChip({ bed }) {
  const key = bed.status.toLowerCase();
  return (
    <span className={`owner-planning__bedchip owner-planning__bedchip--${key}`}>
      <span className={`owner-planning__dot owner-planning__dot--${key}`} />
      {bed.label}
    </span>
  );
}

/* ============================================================
   DRAG PREVIEW
   ============================================================ */

function DragPreview({ drag, rooms }) {
  if (drag.kind === "palette") {
    const t = findRoomType(drag.type);
    return (
      <div className="owner-planning__drag-preview" style={{ background: t.bg, borderColor: t.color }}>
        <div className="owner-planning__palette-beds">
          {Array.from({ length: Math.min(t.beds, 4) }).map((_, i) => (
            <span key={i} style={{ background: t.color }} />
          ))}
        </div>
        <div className="owner-planning__palette-label" style={{ color: t.color }}>
          {t.label} · {t.beds} bed{t.beds > 1 ? "s" : ""}
        </div>
      </div>
    );
  }
  const room = rooms.find((r) => r.id === drag.roomId);
  if (!room) return null;
  const t = findRoomType(room.type);
  return (
    <div className="owner-planning__drag-preview" style={{ borderColor: t.color, background: "#fff" }}>
      <span className="owner-planning__room-typechip" style={{ background: t.bg, color: t.color }}>
        {t.label}
      </span>
      <span className="owner-planning__room-free">{room.beds.length} bed{room.beds.length > 1 ? "s" : ""}</span>
    </div>
  );
}

/* ============================================================
   BULK ADD DIALOG
   ============================================================ */

function BulkAddDialog({ request, floors, rooms, numberingPad, dispatch, onClose }) {
  const [count, setCount] = useState(1);
  const [startNumber, setStartNumber] = useState("");
  const floor = request ? floors.find((f) => f.id === request.floorId) : null;
  const suggested = floor ? nextNumberForFloor(rooms, floor, numberingPad) : "";

  useEffect(() => {
    if (request) {
      setCount(1);
      setStartNumber(suggested);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request?.floorId, request?.type]);

  if (!request) return null;
  const meta = findRoomType(request.type);

  const submit = (n) => {
    const safe = Math.max(1, Math.min(100, Math.round(n)));
    const sequence = floor ? makeNumberSequence(startNumber || suggested, safe, floor) : null;
    const used = new Set(rooms.map((r) => r.number).filter(Boolean));
    const duplicate = sequence?.find((num) => used.has(num));
    if (!sequence) {
      toast.error(`Use the ${floor?.label ?? "floor"} numbering pattern.`, {
        description: floor ? `Next valid number is ${suggested}.` : undefined,
      });
      return;
    }
    if (duplicate) {
      toast.error(`Room ${duplicate} already exists.`, {
        description: `Start from ${suggested} or choose another free range on this floor.`,
      });
      return;
    }
    dispatch({
      type: "ADD_ROOMS_BULK",
      floorId: request.floorId,
      roomType: request.type,
      count: safe,
      opts: { atIndex: request.atIndex, startNumber: sequence[0] },
    });
    toast.success(`Added ${safe} ${meta.label} room${safe > 1 ? "s" : ""}`, {
      description: floor?.label,
    });
    onClose();
  };

  const match = startNumber.trim().match(/^(.*?)(\d+)$/);
  const previewNums = match
    ? Array.from({ length: Math.min(count, 3) }, (_, i) => {
        const n = parseInt(match[2], 10) + i;
        return `${match[1]}${String(n).padStart(match[2].length, "0")}`;
      })
    : null;

  return (
    <Modal
      open
      onClose={onClose}
      title={`Add ${meta.label} rooms${floor ? ` to ${floor.label}` : ""}`}
      size="sm"
      footer={
        <>
          <button className="owner-planning__btn owner-planning__btn--outline" onClick={onClose}>Cancel</button>
          <button className="owner-planning__btn owner-planning__btn--accent" onClick={() => submit(count)}>
            Add {count} room{count > 1 ? "s" : ""}
          </button>
        </>
      }
    >
      <div className="owner-planning__form">
        <label className="owner-planning__label">How many rooms?</label>
        <input
          type="number" min={1} max={100} value={count} autoFocus
          className="owner-planning__input"
          onChange={(e) => setCount(Number(e.target.value) || 1)}
          onKeyDown={(e) => e.key === "Enter" && submit(count)}
        />
        <div className="owner-planning__chip-row">
          {[1, 5, 10, 20].map((n) => (
            <button
              key={n}
              onClick={() => setCount(n)}
              className={`owner-planning__chip ${count === n ? "owner-planning__chip--active" : ""}`}
            >
              +{n}
            </button>
          ))}
        </div>
        <p className="owner-planning__hint">
          Each room gets {meta.beds} bed{meta.beds > 1 ? "s" : ""} and inherits the {meta.label} preset.
        </p>

        <label className="owner-planning__label">Start number (optional)</label>
        <input
          className="owner-planning__input"
          placeholder="e.g. 101 or G01 or A-201"
          value={startNumber}
          onChange={(e) => setStartNumber(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && submit(count)}
        />
        <p className="owner-planning__hint">
          {previewNums
            ? `Will create: ${previewNums.join(", ")}${count > previewNums.length ? "…" : ""}`
            : "Leave blank to auto-number by position on the floor."}
        </p>
      </div>
    </Modal>
  );
}

/* ============================================================
   ROOM EDIT DIALOG
   ============================================================ */

const AMENITY_FIELDS = [
  { key: "washroom",  label: "Washroom",   opts: WASHROOM_OPTIONS },
  { key: "furniture", label: "Furniture",  opts: FURNITURE_OPTIONS },
  { key: "cooling",   label: "Cooling",    opts: COOLING_OPTIONS },
  { key: "geyser",    label: "Geyser",     opts: GEYSER_OPTIONS },
  { key: "washing",   label: "Washing M.", opts: WASHING_OPTIONS },
  { key: "induction", label: "Induction",  opts: INDUCTION_OPTIONS },
  { key: "window",    label: "Window",     opts: WINDOW_OPTIONS },
  { key: "balcony",   label: "Balcony",    opts: BALCONY_OPTIONS },
];

function RoomEditDialog({ room, floors, rooms, amenityPresets, electricityPreset, dispatch, onClose }) {
  const [tab, setTab] = useState("beds");
  const [amen, setAmen] = useState(null);
  const [elec, setElec] = useState(null);

  useEffect(() => {
    if (room) {
      setAmen(room.amenities);
      setElec(room.electricity);
      setTab("beds");
    }
  }, [room?.id]);

  if (!room || !amen || !elec) return null;
  const floor = floors.find((f) => f.id === room.floorId);
  if (!floor) return null;
  const indexOnFloor = rooms.filter((r) => r.floorId === floor.id).findIndex((r) => r.id === room.id);
  const displayNum = room.number || genRoomNumber(floor.prefix, indexOnFloor);

  const save = () => {
    dispatch({ type: "UPDATE_ROOM_AMENITIES", roomId: room.id, amenities: amen });
    dispatch({ type: "UPDATE_ROOM_ELECTRICITY", roomId: room.id, electricity: elec });
    toast.success("Room saved");
    onClose();
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={
        <span className="owner-planning__edit-title">
          <span className="owner-planning__edit-badge">Room {displayNum}</span>
          <span className="owner-planning__edit-meta">{room.type} Sharing · {floor.label}</span>
        </span>
      }
      size="lg"
      footer={
        <>
          <button className="owner-planning__btn owner-planning__btn--outline" onClick={onClose}>Cancel</button>
          <button className="owner-planning__btn owner-planning__btn--accent" onClick={save}>Save Room</button>
        </>
      }
    >
      <div className="owner-planning__tabs">
        <button
          className={`owner-planning__tab ${tab === "beds" ? "owner-planning__tab--active" : ""}`}
          onClick={() => setTab("beds")}
        >
          <BedIcon size={14} /> Beds &amp; Pricing
        </button>
        <button
          className={`owner-planning__tab ${tab === "amen" ? "owner-planning__tab--active" : ""}`}
          onClick={() => setTab("amen")}
        >
          <Sparkles size={14} /> Amenities
        </button>
        <button
          className={`owner-planning__tab ${tab === "elec" ? "owner-planning__tab--active" : ""}`}
          onClick={() => setTab("elec")}
        >
          <Zap size={14} /> Electricity
        </button>
      </div>

      {tab === "beds" && (
        <div className="owner-planning__beds-pane">
          <p className="owner-planning__hint">
            Status and per-bed price. Amenities apply at the room level, not per bed.
          </p>
          {room.beds.map((b) => (
            <div key={b.id} className="owner-planning__bed-row">
              <span className="owner-planning__bed-row-label">{b.label}</span>
              <div>
                <label className="owner-planning__label">Status</label>
                <select
                  className="owner-planning__input"
                  value={b.status}
                  onChange={(e) => dispatch({ type: "UPDATE_BED", roomId: room.id, bedId: b.id, patch: { status: e.target.value } })}
                >
                  {BED_STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="owner-planning__label">Price / Month (₹)</label>
                <input
                  type="number"
                  className="owner-planning__input"
                  value={b.price}
                  onChange={(e) => dispatch({ type: "UPDATE_BED", roomId: room.id, bedId: b.id, patch: { price: Number(e.target.value) || 0 } })}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "amen" && (
        <>
          <div className="owner-planning__preset-bar">
            <div className="owner-planning__preset-bar-text">
              <b>{room.amenitiesOverridden ? "Custom" : "Using preset"}</b>{" "}
              <span>for {room.type} rooms</span>
            </div>
            <button
              className="owner-planning__btn owner-planning__btn--outline owner-planning__btn--sm"
              onClick={() => setAmen({ ...amenityPresets[room.type] })}
            >
              Reset to preset
            </button>
          </div>
          <div className="owner-planning__field-grid">
            {AMENITY_FIELDS.map((f) => (
              <div key={f.key}>
                <label className="owner-planning__label">{f.label}</label>
                <select
                  className="owner-planning__input"
                  value={amen[f.key]}
                  onChange={(e) => setAmen({ ...amen, [f.key]: e.target.value })}
                >
                  {f.opts.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
            ))}
          </div>
        </>
      )}

      {tab === "elec" && (
        <>
          <div className="owner-planning__preset-bar">
            <div className="owner-planning__preset-bar-text">
              <b>{room.electricityOverridden ? "Custom" : "Using PG defaults"}</b>
            </div>
            <button
              className="owner-planning__btn owner-planning__btn--outline owner-planning__btn--sm"
              onClick={() => setElec({ ...electricityPreset })}
            >
              Use PG defaults
            </button>
          </div>
          <div className="owner-planning__field-grid">
            <div>
              <label className="owner-planning__label">Meter No.</label>
              <input className="owner-planning__input" value={elec.meter}
                onChange={(e) => setElec({ ...elec, meter: e.target.value })} />
            </div>
            <div>
              <label className="owner-planning__label">Unit Cost (₹)</label>
              <input type="number" className="owner-planning__input" value={elec.unitCost}
                onChange={(e) => setElec({ ...elec, unitCost: Number(e.target.value) || 0 })} />
            </div>
            <div>
              <label className="owner-planning__label">Previous Reading</label>
              <input type="number" className="owner-planning__input" value={elec.previousReading}
                onChange={(e) => setElec({ ...elec, previousReading: Number(e.target.value) || 0 })} />
            </div>
            <div>
              <label className="owner-planning__label">Current Reading</label>
              <input type="number" className="owner-planning__input" value={elec.currentReading}
                onChange={(e) => setElec({ ...elec, currentReading: Number(e.target.value) || 0 })} />
            </div>
          </div>
          <p className="owner-planning__hint">
            Units consumed: <b>{Math.max(0, elec.currentReading - elec.previousReading)}</b> · Bill ≈ ₹
            <b>{Math.max(0, elec.currentReading - elec.previousReading) * elec.unitCost}</b>
          </p>
        </>
      )}
    </Modal>
  );
}

/* ============================================================
   PRESETS DIALOG
   ============================================================ */

function PresetsDialog({ target, amenityPresets, electricityPreset, dispatch, onClose }) {
  const [amen, setAmen] = useState(null);
  const [elec, setElec] = useState(null);

  useEffect(() => {
    if (!target) return;
    if (target.kind === "amenities") setAmen({ ...amenityPresets[target.type] });
    else setElec({ ...electricityPreset });
  }, [target?.kind, target?.type]);

  if (!target) return null;

  const save = () => {
    if (target.kind === "amenities") {
      dispatch({ type: "SET_AMENITY_PRESET", roomType: target.type, amenities: amen });
      toast.success(`${target.type} amenities preset saved`);
    } else {
      dispatch({ type: "SET_ELECTRICITY_PRESET", electricity: elec });
      toast.success("PG electricity defaults saved");
    }
    onClose();
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={target.kind === "amenities"
        ? `${target.type} Sharing — Amenity Preset`
        : "PG Electricity Defaults"}
      subtitle={`Inherited by every new room of this ${target.kind === "amenities" ? "type" : "PG"}. Per-room overrides stay intact.`}
      size="md"
      footer={
        <>
          <button className="owner-planning__btn owner-planning__btn--outline" onClick={onClose}>Cancel</button>
          <button className="owner-planning__btn owner-planning__btn--accent" onClick={save}>Save Preset</button>
        </>
      }
    >
      {target.kind === "amenities" && amen ? (
        <div className="owner-planning__field-grid">
          {AMENITY_FIELDS.map((f) => (
            <div key={f.key}>
              <label className="owner-planning__label">{f.label}</label>
              <select
                className="owner-planning__input"
                value={amen[f.key]}
                onChange={(e) => setAmen({ ...amen, [f.key]: e.target.value })}
              >
                {f.opts.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
          ))}
        </div>
      ) : elec ? (
        <div className="owner-planning__field-grid">
          <div>
            <label className="owner-planning__label">Unit Cost (₹)</label>
            <input type="number" className="owner-planning__input" value={elec.unitCost}
              onChange={(e) => setElec({ ...elec, unitCost: Number(e.target.value) || 0 })} />
          </div>
          <div>
            <label className="owner-planning__label">Billing Year</label>
            <input type="number" className="owner-planning__input" value={elec.year}
              onChange={(e) => setElec({ ...elec, year: Number(e.target.value) || elec.year })} />
          </div>
          <p className="owner-planning__hint owner-planning__hint--span2">
            Meter number and readings are set per room — they differ across rooms.
          </p>
        </div>
      ) : null}
    </Modal>
  );
}